import { z } from 'zod/v4';
import { isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Translation function type for validation messages.
 * Accepts a dot-path key under the 'auth' namespace.
 */
type T = (key: string) => string;

/**
 * Signup form validation schema factory
 *
 * Validates:
 * - Email format
 * - Password minimum 8 characters
 * - First/last name required
 * - Phone required (must be valid E.164)
 * - Vehicle fields (year, make, model) required for DGR registration
 */
export function createSignupSchema(t: T) {
  return z.object({
    email: z.string().email(t('validation.emailInvalid')),
    password: z.string().min(8, t('validation.passwordMinLength')),
    firstName: z.string().min(1, t('validation.firstNameRequired')),
    lastName: z.string().min(1, t('validation.lastNameRequired')),
    phone: z
      .string()
      .min(1, t('validation.phoneRequired'))
      .refine((val) => isValidPhoneNumber(val), t('validation.phoneInvalid')),
    // Vehicle fields - required for DGR registration
    vehicleYear: z.string().min(1, t('validation.yearRequired')),
    vehicleMake: z.string().min(1, t('validation.makeRequired')),
    vehicleModel: z.string().min(1, t('validation.modelRequired')),
  });
}

export type SignupFormData = z.infer<ReturnType<typeof createSignupSchema>>;

/**
 * Complete registration form validation schema factory
 *
 * Used when an existing auth user (e.g., event-admin) logs in but has no entrant record.
 * Same as signup but without email/password (already authenticated).
 */
export function createCompleteRegistrationSchema(t: T) {
  return z.object({
    firstName: z.string().min(1, t('validation.firstNameRequired')),
    lastName: z.string().min(1, t('validation.lastNameRequired')),
    phone: z
      .string()
      .min(1, t('validation.phoneRequired'))
      .refine((val) => isValidPhoneNumber(val), t('validation.phoneInvalid')),
    vehicleYear: z.string().min(1, t('validation.yearRequired')),
    vehicleMake: z.string().min(1, t('validation.makeRequired')),
    vehicleModel: z.string().min(1, t('validation.modelRequired')),
  });
}

export type CompleteRegistrationFormData = z.infer<
  ReturnType<typeof createCompleteRegistrationSchema>
>;

/**
 * Login form validation schema factory
 *
 * Validates:
 * - Email format
 * - Password required (no min length for login - server validates)
 */
export function createLoginSchema(t: T) {
  return z.object({
    email: z.string().email(t('validation.emailInvalid')),
    password: z.string().min(1, t('validation.passwordRequired')),
  });
}

export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

/**
 * Email check validation schema factory (email-first adaptive auth)
 */
export function createEmailCheckSchema(t: T) {
  return z.object({
    email: z.string().email(t('validation.emailInvalid')),
  });
}

export type EmailCheckFormData = z.infer<ReturnType<typeof createEmailCheckSchema>>;

/**
 * Step 1: Ride selection validation schema factory
 *
 * Validates:
 * - Country code required
 * - State code optional (required for countries with states, handled by UI)
 * - Ride ID required (UUID)
 * - Ride metadata for display
 * - Waiver must be accepted
 * - Waitlist flag for full rides
 */
export function createStep1Schema(t: T) {
  return z.object({
    countryCode: z.string().min(1, t('validation.countryRequired')),
    stateCode: z.string().optional(),
    rideId: z.string().uuid(t('validation.rideRequired')),
    rideName: z.string(),
    rideDate: z.string().optional(),
    rideCity: z.string().optional(),
    waiverAccepted: z.literal(true, { message: t('validation.waiverRequired') }),
    joinWaitlist: z.boolean().default(false),
  });
}

export type Step1FormData = z.infer<ReturnType<typeof createStep1Schema>>;

/**
 * Vehicle selection validation schema factory
 *
 * Validates:
 * - Vehicle type (motorcycle or car)
 * - Make required
 * - Model required
 * - Year optional (1900-2030)
 */
export function createVehicleSchema(t: T) {
  return z.object({
    vehicleType: z.enum(['motorcycle', 'car']),
    vehicleMake: z.string().min(1, t('validation.makeRequired')),
    vehicleModel: z.string().min(1, t('validation.modelRequired')),
    vehicleYear: z.number().int().min(1900).max(2030).optional(),
  });
}

export type VehicleFormData = z.infer<ReturnType<typeof createVehicleSchema>>;
